type RuntimeStep = {
	ts: number;
	step: string;
	data?: string;
};

type RuntimeSession = {
	id: string;
	context: string;
	startedAt: number;
	cleanExit: boolean;
	lastStep: string;
	steps: RuntimeStep[];
};

const KEY = "__mgt_runtime_diag_v1";
const MAX_STEPS = 50;

function canUseStorage(): boolean {
	try {
		return (
			typeof window !== "undefined" &&
			typeof window.localStorage !== "undefined"
		);
	} catch {
		return false;
	}
}

function safeParse(raw: string | null): RuntimeSession | null {
	if (!raw) return null;
	try {
		return JSON.parse(raw) as RuntimeSession;
	} catch {
		return null;
	}
}

function safeStringifyData(data: unknown): string | undefined {
	if (data === undefined) return undefined;
	try {
		const json = JSON.stringify(data);
		return json.length > 500 ? `${json.slice(0, 500)}...` : json;
	} catch {
		return "[unserializable]";
	}
}

function readCurrent(): RuntimeSession | null {
	if (!canUseStorage()) return null;
	try {
		return safeParse(window.localStorage.getItem(KEY));
	} catch {
		return null;
	}
}

function writeCurrent(session: RuntimeSession): void {
	if (!canUseStorage()) return;
	try {
		window.localStorage.setItem(KEY, JSON.stringify(session));
	} catch {
		// ignore quota / private-mode errors
	}
}

function appendStep(step: string, data?: unknown): void {
	const current = readCurrent();
	if (!current) return;

	const serializedData = safeStringifyData(data);

	// skip duplicate consecutive entries (same step + data)
	const lastEntry = current.steps[current.steps.length - 1];
	if (
		lastEntry &&
		lastEntry.step === step &&
		lastEntry.data === serializedData
	) {
		return;
	}

	const next: RuntimeSession = {
		...current,
		lastStep: step,
		steps: [
			...current.steps,
			{
				ts: Date.now(),
				step,
				data: serializedData,
			},
		].slice(-MAX_STEPS),
	};

	writeCurrent(next);
}

export function initRuntimeDiagnostics(context: string): RuntimeSession | null {
	try {
		if (!canUseStorage()) return null;

		const previous = readCurrent();
		const next: RuntimeSession = {
			id: `${Date.now()}-${Math.random().toString(36).slice(2, 8)}`,
			context,
			startedAt: Date.now(),
			cleanExit: false,
			lastStep: "session_started",
			steps: [{ ts: Date.now(), step: "session_started" }],
		};

		writeCurrent(next);

		const markCleanExit = () => {
			const current = readCurrent();
			if (!current) return;
			current.cleanExit = true;
			current.lastStep = "session_clean_exit";
			current.steps = [
				...current.steps,
				{ ts: Date.now(), step: "session_clean_exit" },
			].slice(-MAX_STEPS);
			writeCurrent(current);
		};

		window.addEventListener("pagehide", markCleanExit, { once: true });

		return previous && previous.cleanExit === false ? previous : null;
	} catch {
		return null;
	}
}

export function markRuntimeStep(step: string, data?: unknown): void {
	try {
		appendStep(step, data);
	} catch {
		// diagnostics must never throw
	}
}

export function markRuntimeError(step: string, error: unknown): void {
	try {
		appendStep(`${step}_error`, {
			message: error instanceof Error ? error.message : String(error),
		});
	} catch {
		// diagnostics must never throw
	}
}
